import { useCallback, useMemo, useState } from "react";

import {
  collectFolderPaths,
  isWithinFolder,
  normalizeFolderPath,
  parentFolderOf,
} from "@domain/notebook-folders";
import { deriveNoteFileName, validateNotebookPath } from "@domain/notebook-path";
import {
  notebookFolderOpFailed,
  notebookInvalidFolderName,
} from "@domain/user-errors";
import type { UseNotebook } from "@/hooks/useNotebook";

/**
 * Folder operations over the notebook action surface (re-land spec D1–D4).
 *
 * Folders are DERIVED: a note's folder IS `parentFolderOf(note.path)` — there
 * is no folder table and no folder object in GCS. "New folder" therefore only
 * registers a session-local path (`ephemeralFolders`) so create-then-drag
 * works; an empty folder vanishes on reload, and one holding notes is
 * re-derived from their paths.
 *
 * Every multi-note operation is a sequential loop of single-note moves, each
 * move being write-new-THEN-delete-old (`saveNote{fromPath}` merge-preserves
 * `createdBy`/`createdAt` server-side). On the first failure the loop STOPS,
 * the list refreshes, and `error` names the failed path — a crash or failure
 * can leave a note at both paths, never at neither.
 */
export interface UseNotebookFolders {
  /** Sorted, ancestor-complete folder paths: note-derived ∪ ephemeral. */
  folders: string[];
  /** Session-local folders with no notes (yet) — lost on reload by design. */
  ephemeralFolders: string[];
  /** Validates + registers a session-local folder ("Projects/Q1" nests).
   *  Returns the normalized path, or null (with `error` set) on a bad name. */
  createFolder: (path: string) => string | null;
  /** Move one note into `folder` ("" = root). Returns the note's new path, or
   *  null on failure (list refreshed, `error` set). */
  moveNote: (args: { path: string; folder: string }) => Promise<string | null>;
  /** Rename a folder — moves every contained note. False on failure. */
  renameFolder: (args: { path: string; newPath: string }) => Promise<boolean>;
  /** Delete a folder by moving its notes (flattened) to its parent — never
   *  deletes a note. Name collisions get a ` (2)`-style suffix. */
  deleteFolder: (path: string) => Promise<boolean>;
  /** Import parsed markdown files as notes into `folder` ("" = root), one
   *  `saveNote` per file with ` (2)`-style collision suffixes against existing
   *  notes AND the batch itself. Per-file failures don't stop the loop — the
   *  result names them so the modal can report "imported 8 of 10". Returns
   *  null only when the folder name is invalid (`error` set) or an op is
   *  already in flight. */
  importNotes: (args: {
    files: Array<{ title: string; content: string }>;
    folder: string;
  }) => Promise<ImportNotesResult | null>;
  /** True while a folder-op loop is in flight (ops are rejected meanwhile). */
  busy: boolean;
  /** Copy for the most recent folder-op failure, or null. */
  error: string | null;
}

export interface ImportNotesResult {
  imported: number;
  /** Display titles of the files that could not be imported. */
  failed: string[];
}

/** A folder name is valid iff a note filed under it validates — probe with a
 *  synthetic filename rather than duplicating the path rules (spec §5). */
function isValidFolderPath(folder: string): boolean {
  if (folder === "") return true;
  return validateNotebookPath({ path: `${folder}/probe.md` }).isOk();
}

function composeNotePath(folder: string, fileName: string): string {
  return folder ? `${folder}/${fileName}` : fileName;
}

/** First free destination for `fileName` in `folder`: the name itself, then
 *  `name (2).md`, `name (3).md`… against `taken` (existing + already-assigned
 *  targets), so a folder-delete flatten never overwrites a note. */
function resolveCollisionFreePath(
  folder: string,
  fileName: string,
  taken: ReadonlySet<string>,
): string | null {
  const direct = composeNotePath(folder, fileName);
  if (!taken.has(direct)) return direct;
  const stem = fileName.replace(/\.md$/i, "");
  for (let n = 2; n < 100; n++) {
    const derived = deriveNoteFileName({ title: `${stem} (${n})` });
    if (derived.isErr()) return null;
    const candidate = composeNotePath(folder, derived.value);
    if (!taken.has(candidate)) return candidate;
  }
  return null;
}

export function useNotebookFolders(notebook: UseNotebook): UseNotebookFolders {
  const { notes, refresh, readNote, saveNote, deleteNote } = notebook;

  const [ephemeralFolders, setEphemeralFolders] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const folders = useMemo(
    () =>
      collectFolderPaths(
        ephemeralFolders,
        notes.map((n) => parentFolderOf(n.path)),
      ),
    [ephemeralFolders, notes],
  );

  /** One write-before-delete note move. Throws on failure (caller stops). */
  const moveOne = useCallback(
    async (fromPath: string, toPath: string): Promise<void> => {
      if (fromPath === toPath) return;
      const note = await readNote({ path: fromPath });
      await saveNote({ path: toPath, content: note.content, fromPath });
      await deleteNote({ path: fromPath });
    },
    [readNote, saveNote, deleteNote],
  );

  const createFolder = useCallback((rawPath: string): string | null => {
    const path = normalizeFolderPath(rawPath);
    if (!path || !isValidFolderPath(path)) {
      setError(notebookInvalidFolderName());
      return null;
    }
    setError(null);
    setEphemeralFolders((prev) => (prev.includes(path) ? prev : [...prev, path]));
    return path;
  }, []);

  const moveNote = useCallback(
    async ({ path, folder }: { path: string; folder: string }): Promise<string | null> => {
      if (busy) return null;
      const fileName = path.split("/").pop() ?? path;
      const taken = new Set(notes.map((n) => n.path));
      taken.delete(path); // moving onto itself is a no-op, not a collision
      const toPath = resolveCollisionFreePath(folder, fileName, taken);
      if (toPath === null) {
        setError(notebookFolderOpFailed({ failedPath: path }));
        return null;
      }
      if (toPath === path) return path;
      setBusy(true);
      setError(null);
      try {
        await moveOne(path, toPath);
        return toPath;
      } catch {
        setError(notebookFolderOpFailed({ failedPath: path }));
        return null;
      } finally {
        setBusy(false);
        await refresh();
      }
    },
    [busy, notes, moveOne, refresh],
  );

  const renameFolder = useCallback(
    async ({ path, newPath: rawNewPath }: { path: string; newPath: string }): Promise<boolean> => {
      if (busy) return false;
      const newPath = normalizeFolderPath(rawNewPath);
      if (!newPath || !isValidFolderPath(newPath) || isWithinFolder(newPath, path)) {
        setError(notebookInvalidFolderName());
        return false;
      }
      if (newPath === path) return true;
      const contained = notes.filter((n) => isWithinFolder(parentFolderOf(n.path), path));
      setBusy(true);
      setError(null);
      try {
        // Sequential, bounded by MAX_NOTE_COUNT. Nested paths keep their
        // structure: `path/sub/x.md` → `newPath/sub/x.md`.
        for (const note of contained) {
          try {
            await moveOne(note.path, `${newPath}${note.path.slice(path.length)}`);
          } catch {
            setError(notebookFolderOpFailed({ failedPath: note.path }));
            return false;
          }
        }
        setEphemeralFolders((prev) =>
          prev.map((f) => (isWithinFolder(f, path) ? `${newPath}${f.slice(path.length)}` : f)),
        );
        // The renamed folder must survive even when it held no notes.
        setEphemeralFolders((prev) => (prev.includes(newPath) ? prev : [...prev, newPath]));
        return true;
      } finally {
        setBusy(false);
        await refresh();
      }
    },
    [busy, notes, moveOne, refresh],
  );

  const deleteFolder = useCallback(
    async (path: string): Promise<boolean> => {
      if (busy || !path) return false;
      const dest = parentFolderOf(path);
      const contained = notes.filter((n) => isWithinFolder(parentFolderOf(n.path), path));
      const taken = new Set(notes.map((n) => n.path));
      setBusy(true);
      setError(null);
      try {
        // Notes flatten into the parent folder (delete never deletes a note).
        for (const note of contained) {
          const fileName = note.path.split("/").pop() ?? note.path;
          const toPath = resolveCollisionFreePath(dest, fileName, taken);
          if (toPath === null) {
            setError(notebookFolderOpFailed({ failedPath: note.path }));
            return false;
          }
          try {
            await moveOne(note.path, toPath);
          } catch {
            setError(notebookFolderOpFailed({ failedPath: note.path }));
            return false;
          }
          taken.delete(note.path);
          taken.add(toPath);
        }
        setEphemeralFolders((prev) => prev.filter((f) => !isWithinFolder(f, path)));
        return true;
      } finally {
        setBusy(false);
        await refresh();
      }
    },
    [busy, notes, moveOne, refresh],
  );

  const importNotes = useCallback(
    async ({
      files,
      folder,
    }: {
      files: Array<{ title: string; content: string }>;
      folder: string;
    }): Promise<ImportNotesResult | null> => {
      if (busy) return null;
      let dest = "";
      if (folder) {
        const created = createFolder(folder);
        if (created === null) return null; // invalid name — `error` is set
        dest = created;
      }
      setBusy(true);
      setError(null);
      try {
        const taken = new Set(notes.map((n) => n.path));
        const failed: string[] = [];
        let imported = 0;
        for (const file of files) {
          const label = file.title.trim() || "Untitled";
          const derived = deriveNoteFileName({ title: file.title });
          if (derived.isErr()) {
            failed.push(label);
            continue;
          }
          const toPath = resolveCollisionFreePath(dest, derived.value, taken);
          if (toPath === null) {
            failed.push(label);
            continue;
          }
          try {
            await saveNote({ path: toPath, content: file.content });
            taken.add(toPath);
            imported++;
          } catch {
            // Per-file failure — keep importing the rest; the result reports it.
            failed.push(label);
          }
        }
        return { imported, failed };
      } finally {
        setBusy(false);
        await refresh();
      }
    },
    [busy, notes, createFolder, saveNote, refresh],
  );

  return {
    folders,
    ephemeralFolders,
    createFolder,
    moveNote,
    renameFolder,
    deleteFolder,
    importNotes,
    busy,
    error,
  };
}
